// WEB: módulo nativo tirvu-rede → shim web (sempre "indisponível": o browser não escolhe
// a interface de rede; mesmos membros `disponivel`/`redeAtualEhWifi`/`requisicao`).
import DadosMoveis from "@/platform/tirvu-rede";
import AsyncStorage from "@/platform/async-storage";
import { Environment } from "../utils/Environment";
import { RelogioConfiavel } from "./RelogioConfiavel";

/**
 * API do PRESTADOR (freelancer) — cliente HTTP AUTÔNOMO.
 *
 * ⚠️ NÃO reaproveita o `apiRequest` do Api.tsx de propósito: aquele injeta o
 * Bearer do COLABORADOR (`@tirvumais:token`). Se o prestador usasse o mesmo
 * caminho, uma sessão contaminaria a outra — e os ids das duas tabelas
 * (tab_adm_colaboradores × tab_adm_colaboradores_freelancers) COLIDEM no mesmo
 * banco. Token, chaves de storage, geração de sessão e rotas são separados.
 *
 * Backend: t-credentials, base /api/PrestadorLogin (claim tp='F' no JWT).
 */

type ApiDone = any;
type ApiErros = string[] | null;
type QueryValue = string | number | boolean | null | undefined;

// ── Chaves EXCLUSIVAS do prestador (nunca as do colaborador) ─────────────────
export const PRESTADOR_TOKEN_KEY = "@tirvumais:prestador_token";
export const PRESTADOR_ID_KEY = "@tirvumais:prestador_id";
export const PRESTADOR_HASH_KEY = "@tirvumais:prestador_hash";
export const PRESTADOR_CPF_KEY = "@tirvumais:prestador_cpf";
export const PRESTADOR_DADOS_KEY = "@tirvumais:prestador_dados";

let prestadorToken: string | null = null;

export const setPrestadorAuthToken = (token: string | null) => {
  prestadorToken = token?.trim() ? token.trim() : null;
};
export const clearPrestadorAuthToken = () => {
  prestadorToken = null;
};

/** Geração de sessão do PRESTADOR — espelha o padrão do colaborador (guarda
 *  anti-ressurreição de gravação assíncrona após signIn/signOut/trocarEmpresa). */
let prestadorSessionGen = 0;
export const bumpPrestadorSessionGen = () => {
  prestadorSessionGen += 1;
};
export const peekPrestadorSessionGen = () => prestadorSessionGen;

const getPrestadorToken = async () => {
  if (prestadorToken?.trim()) return prestadorToken.trim();
  const stored = await AsyncStorage.getItem(PRESTADOR_TOKEN_KEY);
  prestadorToken = stored?.trim() ? stored.trim() : null;
  return prestadorToken;
};

/** Existe sessão de PRESTADOR (token) neste aparelho? Usado por quem envia sem tela aberta. */
export const possuiSessaoPrestador = async (): Promise<boolean> => {
  try {
    return !!(await getPrestadorToken());
  } catch {
    return false;
  }
};

/**
 * IDENTIDADE DA SESSÃO DO PRESTADOR, lida do TOKEN (nunca do cache).
 *
 * `c_hash` = tenant, `nameid` = id_adm_colaborador_freelancer, `sub` = CPF.
 * Memoizado por token (o parse é barato, mas isto é chamado em todo acesso a dado
 * local).
 */
export type IdentidadePrestador = {
  hash: string;
  idPrestador: number;
  cpf: string;
};

const IDENTIDADE_VAZIA: IdentidadePrestador = {
  hash: "",
  idPrestador: 0,
  cpf: "",
};

let _identidadeMemo: { token: string; dados: IdentidadePrestador } | null = null;

const decodificarPayload = (token: string): any | null => {
  try {
    const parts = String(token).split(".");
    if (parts.length < 2) return null;
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const ALFA =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
    let bin = "";
    for (let i = 0; i < b64.length; i += 4) {
      const n =
        (ALFA.indexOf(b64[i]) << 18) |
        (ALFA.indexOf(b64[i + 1]) << 12) |
        ((ALFA.indexOf(b64[i + 2]) & 63) << 6) |
        (ALFA.indexOf(b64[i + 3]) & 63);
      bin += String.fromCharCode((n >> 16) & 255);
      if (b64[i + 2] !== "=") bin += String.fromCharCode((n >> 8) & 255);
      if (b64[i + 3] !== "=") bin += String.fromCharCode(n & 255);
    }
    return JSON.parse(bin);
  } catch {
    return null;
  }
};

export const identidadePrestador = async (): Promise<IdentidadePrestador> => {
  const token = await getPrestadorToken();
  if (!token) return IDENTIDADE_VAZIA;
  if (_identidadeMemo?.token === token) return _identidadeMemo.dados;

  const payload = decodificarPayload(token);
  if (!payload) return IDENTIDADE_VAZIA;

  const dados: IdentidadePrestador = {
    hash: String(payload?.c_hash ?? "")
      .trim()
      .toUpperCase(),
    idPrestador: Number(payload?.nameid) || 0,
    cpf: String(payload?.sub ?? "").replace(/\D/g, ""),
  };
  _identidadeMemo = { token, dados };
  return dados;
};

/** Tenant da sessão (claim c_hash). */
export const tenantHashPrestador = async (): Promise<string> =>
  (await identidadePrestador()).hash;

/**
 * ESCOPO dos dados locais do prestador: **tenant + pessoa**.
 *
 * ⚠️ Chave escopada só por tenant foi um bug GRAVE: dois CPFs diferentes da MESMA
 * empresa compartilhavam o espelho, e quem logava depois via a diária do anterior.
 * Tenant sozinho identifica a EMPRESA, não QUEM está logado.
 *
 * Formato `HASH:ID`. Sem o id (token antigo/estranho), cai para `HASH:cpfNNN` — o
 * importante é nunca voltar a um escopo compartilhado, e nunca BLOQUEAR o registro
 * por falta de um id.
 */
export const escopoPrestador = async (): Promise<string> => {
  const { hash, idPrestador, cpf } = await identidadePrestador();
  if (!hash) return "";
  if (idPrestador > 0) return `${hash}:${idPrestador}`;
  if (cpf) return `${hash}:cpf${cpf}`;
  return "";
};

const ApiReturn = (
  success: boolean,
  erros: ApiErros,
  done: ApiDone = null,
  offline = false,
) => ({ success, erros, done, offline });

const DEFAULT_TIMEOUT_MS = 15000;

const buildUrl = (
  path: string,
  query?: Record<string, QueryValue>,
  baseUrl?: string,
) => {
  // Default = t-credentials (login/leituras). Ingestão (gravar diária) passa o
  // host do T-ARC explicitamente — a mesma divisão do app do colaborador.
  const raiz = baseUrl?.trim() || Environment.T_CREDENTIALS_BASE;
  const base = raiz.endsWith("/") ? raiz : `${raiz}/`;
  const url = new URL(path.replace(/^\//, ""), base);
  if (query) {
    Object.entries(query).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      const s = String(v).trim();
      if (s) url.searchParams.append(k, s);
    });
  }
  return url.toString();
};

const parseBody = async (response: Response) => {
  const ct = response.headers.get("content-type") || "";
  if (ct.includes("json")) return response.json();
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const extrairErros = (payload: any, fallback: string) => {
  if (!payload) return [fallback];
  if (Array.isArray(payload)) return payload.map((i) => String(i));
  if (typeof payload === "string") return [payload];
  const cands = [
    payload?.mensagem, // t-credentials responde { mensagem }
    payload?.detail,
    payload?.title,
    payload?.message,
    payload?.error,
  ].filter(Boolean);
  return cands.length ? cands.map((i) => String(i)) : [fallback];
};

const requisicao = async <T = any,>({
  path,
  method = "POST",
  body,
  autenticado = false,
  timeoutMs,
  baseUrl,
}: {
  path: string;
  method?: "GET" | "POST";
  body?: any;
  /** true → envia o Bearer do PRESTADOR (nunca o do colaborador). */
  autenticado?: boolean;
  timeoutMs?: number;
  /** Host alternativo (ex.: T-ARC para gravar). Omitido = t-credentials. */
  baseUrl?: string;
}) => {
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(),
    timeoutMs ?? DEFAULT_TIMEOUT_MS,
  );
  try {
    const headers: Record<string, string> = {
      Accept: "application/json, text/json, text/plain",
      "Content-Type": "application/json",
    };
    if (autenticado) {
      const t = await getPrestadorToken();
      if (t) headers.Authorization = `Bearer ${t}`;
    }
    // Uptime antes do fetch: o RelogioConfiavel ancora o header `Date` no meio do
    // round-trip para descontar a latência.
    const uptimeAntes = RelogioConfiavel.uptimeAtual();
    const response = await fetch(buildUrl(path, undefined, baseUrl), {
      method,
      headers,
      signal: controller.signal,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    // HORA CONFIÁVEL: o relógio é do APARELHO, não da sessão — reancorar aqui também
    // mantém a hora correta para quem só usa o app como prestador (a sessão do
    // colaborador pode nem existir neste aparelho, e aí o Api.tsx nunca roda).
    RelogioConfiavel.sincronizarComHeader(
      response.headers.get("date"),
      uptimeAntes,
    );
    const ehJson = (response.headers.get("content-type") || "")
      .toLowerCase()
      .includes("json");
    const payload = await parseBody(response);
    if (!response.ok) {
      // Mesma classificação do app do colaborador: transitório → offline (retry),
      // 4xx de negócio → rejeição definitiva. Corpo que NÃO é JSON num erro não veio
      // da nossa API (proxy, página de manutenção, portal de wi-fi): também é offline.
      const offline =
        !ehJson || [408, 429, 500, 502, 503, 504].includes(response.status);
      return ApiReturn(
        false,
        extrairErros(payload, "Não foi possível concluir a operação."),
        payload,
        offline,
      );
    }
    // 2xx com PÁGINA (text/html) não é a nossa API — é quem está no meio do caminho
    // (portal de wi-fi cativo, página de bloqueio do órgão). Aceitar como sucesso
    // marcava a diária como enviada sem ela ter saído do aparelho.
    if (
      (response.headers.get("content-type") || "")
        .toLowerCase()
        .includes("text/html")
    ) {
      return ApiReturn(
        false,
        ["A rede devolveu uma página no lugar da API"],
        null,
        true,
      );
    }
    return ApiReturn(true, null, payload as T);
  } catch (error: any) {
    const isTimeout = error?.name === "AbortError";
    return ApiReturn(
      false,
      [
        isTimeout
          ? "Tempo de conexão esgotado"
          : // WEB: erro de rede/CORS no browser é `TypeError` com mensagem em inglês
            // (varia por engine) — mapeia para a mensagem pt-BR, igual ao Api.tsx.
            error instanceof TypeError
            ? "Falha ao conectar com a API"
            : error?.message || "Falha ao conectar com a API",
      ],
      null,
      true,
    );
  } finally {
    clearTimeout(timer);
  }
};

/**
 * POST pela rede CELULAR (Android), com o Bearer do PRESTADOR — para quando o Wi-Fi atual
 * não alcança a nossa API (rede de órgão que deixa o Google passar e bloqueia os nossos
 * hosts). Mesma classificação da `requisicao`: sem resposta, página HTML ou 5xx = offline.
 * iOS / build sem o módulo: devolve offline e nada muda.
 */
const requisicaoPorDadosMoveis = async ({
  path,
  body,
  baseUrl,
  timeoutMs,
}: {
  path: string;
  body: any;
  baseUrl?: string;
  timeoutMs?: number;
}) => {
  try {
    const headers: Record<string, string> = {
      Accept: "application/json, text/json, text/plain",
      "Content-Type": "application/json",
    };
    const t = await getPrestadorToken();
    if (t) headers.Authorization = `Bearer ${t}`;
    const r = await DadosMoveis.requisicao({
      url: buildUrl(path, undefined, baseUrl),
      metodo: "POST",
      cabecalhos: headers,
      corpo: JSON.stringify(body),
      timeoutMs: timeoutMs ?? DEFAULT_TIMEOUT_MS,
    });
    if (!r.ok) {
      return ApiReturn(
        false,
        [r.mensagem || "Sem acesso pelos dados móveis"],
        null,
        true,
      );
    }
    const tipo = r.tipoConteudo.toLowerCase();
    let payload: any = null;
    if (r.corpo) {
      try {
        payload = JSON.parse(r.corpo);
      } catch {
        payload = r.corpo;
      }
    }
    if (r.status >= 200 && r.status < 300) {
      if (tipo.includes("text/html")) {
        return ApiReturn(
          false,
          ["A rede devolveu uma página no lugar da API"],
          null,
          true,
        );
      }
      return ApiReturn(true, null, payload);
    }
    const offline =
      !tipo.includes("json") ||
      [408, 429, 500, 502, 503, 504].includes(r.status);
    return ApiReturn(
      false,
      extrairErros(payload, `Falha (HTTP ${r.status}) pelos dados móveis`),
      payload,
      offline,
    );
  } catch (e: any) {
    return ApiReturn(
      false,
      [e?.message || "Falha ao enviar pelos dados móveis"],
      null,
      true,
    );
  }
};

const BASE = "api/PrestadorLogin";
/** Operação da diária — domínio separado do login (mesma separação no backend). */
const BASE_DIARIA = "api/PrestadorDiaria";
/** Cadastro próprio do prestador (perfil / "Meus Dados"). */
const BASE_PERFIL = "api/PrestadorPerfil";

/**
 * Identidade do prestador = `id_adm_colaborador_freelancer`.
 *
 * O nome do campo varia conforme a origem (o backend novo manda `idPrestador`,
 * a lista de empresas manda `id_adm_colaborador`, e o alias `idColaborador`
 * existe por compatibilidade). Ler "na mão" em cada tela é como o cartão passou
 * a mostrar **ID 0**: a tela lia só `idColaborador`, que o login nunca devolveu.
 * Passe as fontes na ordem de confiança — a 1ª que tiver um id > 0 vence.
 */
export const extrairIdPrestador = (...fontes: any[]): number => {
  for (const f of fontes) {
    if (!f) continue;
    const bruto =
      f?.idPrestador ??
      f?.IdPrestador ??
      f?.idColaborador ??
      f?.id_adm_colaborador_freelancer ??
      f?.id_adm_colaborador ??
      f?.id ??
      f?.codigo;
    const n = Number(bruto);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};

/** 1º texto não-vazio da lista (evita gravar "" por cima de um valor bom). */
export const primeiroTexto = (
  ...valores: (string | null | undefined)[]
): string | undefined => {
  for (const v of valores) {
    const s = String(v ?? "").trim();
    if (s) return s;
  }
  return undefined;
};

export const ApiPrestador = {
  autenticacao: {
    /** Empresas do CPF onde o prestador tem permite_ponto = 'S'. */
    listarEmpresas: async (payload: { cpf?: string | null }) => {
      const cpf = String(payload?.cpf ?? "").trim();
      if (!cpf) return ApiReturn(false, ["CPF não informado"], null);
      return requisicao({ path: `${BASE}/listarEmpresas`, body: { cpf } });
    },

    /** Tem senha? Se não, devolve as opções de nome do 1º acesso. */
    verificarSenha: async (payload: {
      cpf?: string | null;
      hash?: string | null;
      idPrestador?: number | null;
    }) => {
      if (!String(payload?.hash ?? "").trim())
        return ApiReturn(false, ["Hash não informado"], null);
      return requisicao({ path: `${BASE}/verificarSenha`, body: payload });
    },

    opcoesNome: async (payload: {
      idPrestador?: number | null;
      hash?: string | null;
    }) => requisicao({ path: `${BASE}/opcoesNome`, body: payload }),

    /**
     * 1º acesso. `parteNome` é a PROVA DE IDENTIDADE e é validada NO SERVIDOR —
     * a conferência da tela sozinha não vale nada para quem chama a API direto.
     */
    cadastrarSenha: async (payload: {
      idPrestador?: number | null;
      hash?: string | null;
      novaSenha?: string | null;
      parteNome?: string | null;
    }) => requisicao({ path: `${BASE}/cadastrarSenha`, body: payload }),

    /** Zera a senha → o próximo acesso cai no fluxo de 1º acesso. */
    recuperarSenha: async (payload: {
      cpf?: string | null;
      hash?: string | null;
    }) => requisicao({ path: `${BASE}/recuperarSenha`, body: payload }),

    consultarCpf: async (payload: {
      cpf?: string | null;
      hash?: string | null;
    }) => requisicao({ path: `${BASE}/consultarCpf`, body: payload }),

    /** Emite o token com claim tp='F'. */
    login: async (payload: {
      cpf?: string | null;
      senha?: string | null;
      hash?: string | null;
      idPrestador?: number | null;
    }) => {
      if (!String(payload?.hash ?? "").trim())
        return ApiReturn(false, ["Hash não informado"], null);
      return requisicao({ path: `${BASE}/login`, body: payload });
    },

    /** Troca de empresa (CPF vem do token). Autenticada. */
    trocarEmpresa: async (hash: string, idPrestador?: number | null) =>
      requisicao({
        path: `${BASE}/trocarEmpresa`,
        body: { hash, idPrestador: idPrestador ?? 0 },
        autenticado: true,
      }),

    /** Troca de senha do próprio prestador (id/tenant vêm do token). */
    alterarSenha: async (novaSenha: string) =>
      requisicao({
        path: `${BASE}/alterarSenha`,
        body: { novaSenha },
        autenticado: true,
      }),
  },

  perfil: {
    /** Bancos do tenant (seletor de conta bancária). */
    bancos: async () =>
      requisicao({
        path: `${BASE_PERFIL}/bancos`,
        method: "GET",
        autenticado: true,
        timeoutMs: 8000,
      }),

    /** Salva contato + dados bancários (PIX e conta são EXCLUDENTES). */
    salvar: async (payload: {
      telefone?: string;
      dadosBancarios: {
        temPix: boolean;
        tipoChavePix?: string;
        chavePix?: string;
        idBanco?: number;
        agencia?: string;
        conta?: string;
      };
    }) =>
      requisicao({
        path: `${BASE_PERFIL}/salvar`,
        body: payload,
        autenticado: true,
      }),

    /** Foto de perfil em BASE64 (rota tem parser próprio de 8mb no backend). */
    alterarFoto: async (fotoBase64: string) =>
      requisicao({
        path: `${BASE_PERFIL}/alterarFoto`,
        body: { foto: fotoBase64 },
        autenticado: true,
        timeoutMs: 45000,
      }),

    /**
     * Cadastro ATUAL do prestador logado (nome, cargo, CPF, foto).
     *
     * É o que faz o app atualizar o perfil sem relogar — foto inserida direto no
     * banco aparece no refresh. Id e tenant saem do TOKEN.
     */
    carregar: async () =>
      requisicao({
        path: `${BASE_PERFIL}/carregar`,
        method: "GET",
        autenticado: true,
        timeoutMs: 8000,
      }),
  },

  diaria: {
    /**
     * Postos onde a diária pode ser registrada (clientes ativos que não são ponto
     * de apoio). O TENANT sai do token — nada de banco no body.
     *
     * Timeout curto de propósito: a tela abre o seletor com a cópia local e esta
     * chamada só atualiza. Quem está sem rede não pode ficar 15s olhando spinner
     * para registrar uma diária offline.
     */
    listarPostos: async () =>
      requisicao({
        path: `${BASE_DIARIA}/listarPostos`,
        method: "GET",
        autenticado: true,
        timeoutMs: 8000,
      }),

    /**
     * A diária deste DIA existe no SERVIDOR? (leitura → t-credentials)
     *
     * É o que permite o app DESFAZER um "registrada" local quando a linha não está
     * mais no banco. Timeout curto: quem está sem rede não pode ficar esperando —
     * falha aqui simplesmente NÃO reconcilia (o espelho continua manda).
     */
    consultarDia: async (competencia: string) =>
      requisicao({
        path: `${BASE_DIARIA}/diariaDoDia`,
        body: { competencia },
        autenticado: true,
        timeoutMs: 8000,
      }),

    /**
     * Grava a diária (T-ARC → RabbitMQ → T-Consumer → tab_rh_pontos).
     *
     * ⚠️ Vai para o T-ARC (ingestão), NÃO para o t-credentials: escrita de ponto é
     * domínio do T-ARC. `foto` é BASE64 — o T-ARC sobe para o storage (prefixo F_)
     * e publica só o link, igual ao ponto do CLT.
     *
     * Timeout FOLGADO: o corpo carrega a selfie e o upload no servidor é síncrono.
     * Curto demais aqui viraria retry eterno de uma requisição que ia dar certo.
     */
    registrar: async (payload: Record<string, any>) =>
      requisicao({
        path: "api/PrestadorDiaria/registrar",
        body: payload,
        autenticado: true,
        baseUrl: Environment.T_ARC_BASE,
        timeoutMs: 45000,
      }),

    /** A mesma gravação pelos DADOS MÓVEIS (Android) — ver drenarDiarias. */
    registrarPorDadosMoveis: async (payload: Record<string, any>) =>
      requisicaoPorDadosMoveis({
        path: "api/PrestadorDiaria/registrar",
        body: payload,
        baseUrl: Environment.T_ARC_BASE,
        timeoutMs: 45000,
      }),
  },
};

export default ApiPrestador;
